import { FC, useCallback, useMemo } from 'react';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Typography } from '@altermaind/web/ui';
import { Form } from '@/_shared/components/Form';
import { useDialog } from '@/_shared/hooks/useDialog';
import { ContentType, Filter } from '@/swagger/constants/contents-orchestrator-enums';

type CheckboxItem<T> = { label: string; value: T };

type FiltersProps = {
  filters?: `${Filter}`[];
  filterByTypes?: `${ContentType}`[];
};

type Props = {
  onlyFilterByStateFilter?: boolean;
  appliedFilters?: FiltersProps;
  onChangeFilters: (filters: FiltersProps) => void;
  hideStatusFilter?: boolean;
};

export const FilterBy: FC<Props> = ({
  appliedFilters,
  onChangeFilters,
  onlyFilterByStateFilter,
  hideStatusFilter = false,
}) => {
  const t = useTranslations('trainingSoftSkills.filtersAndOrderContent');
  const { closeDialog: closeDialogFilters } = useDialog('filters');

  const { handleSubmit, control, watch } = useForm({
    defaultValues: {
      filterByTypes: appliedFilters?.filterByTypes,
      filtersByState: appliedFilters?.filters,
    },
  });

  const fieldValues = watch();

  const onSubmit: SubmitHandler<FieldValues> = useCallback(() => {
    const selectedType = Array.isArray(fieldValues.filterByTypes)
      ? (fieldValues.filterByTypes as unknown as CheckboxItem<`${ContentType}`>[]).map(
          item => item.value,
        )
      : undefined;

    const selectedState = Array.isArray(fieldValues.filtersByState)
      ? (fieldValues.filtersByState as unknown as CheckboxItem<`${Filter}`>[]).map(
          item => item.value,
        )
      : undefined;

    onChangeFilters({
      filterByTypes: !onlyFilterByStateFilter ? selectedType : undefined,
      filters: selectedState,
    });
    closeDialogFilters();
  }, [
    fieldValues.filterByTypes,
    fieldValues.filtersByState,
    onChangeFilters,
    onlyFilterByStateFilter,
    closeDialogFilters,
  ]);

  const radioItems = useMemo(() => {
    return Object.keys(Filter)
      .map(key => {
        if (
          Filter[key as keyof typeof Filter] === Filter.ISCOMPLETED ||
          Filter[key as keyof typeof Filter] === Filter.ISSAVED
        ) {
          return {
            label: t(`filters.state.${Filter[key as keyof typeof Filter]}`),
            value: Filter[key as keyof typeof Filter],
          };
        }
        return undefined;
      })
      .filter(item => item !== undefined);
  }, [t]);

  const radioItemsType = useMemo(() => {
    return Object.keys(ContentType).map(key => {
      return {
        label: t(`filters.type.${ContentType[key as keyof typeof ContentType]}`),
        value: ContentType[key as keyof typeof ContentType],
      };
    });
  }, [t]);

  return (
    <Box>
      <Box display='flex' flexDirection='column'>
        <form onSubmit={handleSubmit(onSubmit)}>
          {!hideStatusFilter && (
            <Box>
              <Typography mb='xxs' variant='p2/light'>
                {t('filters.state.placeHolder')}
              </Typography>
              <Form.FormDropdownMultiple
                zIndex={300}
                // @ts-ignore
                control={control}
                name='filtersByState'
                items={radioItems || []}
              />
            </Box>
          )}
          {!onlyFilterByStateFilter ? (
            <Box>
              <Typography mt={hideStatusFilter ? 'none' : 's'} mb='xxs' variant='p2/light'>
                {t('filters.type.placeHolder')}
              </Typography>
              <Form.FormDropdownMultiple
                zIndex={300}
                // @ts-ignore
                control={control}
                name='filterByTypes'
                items={radioItemsType || []}
              />
            </Box>
          ) : null}
          <Box justifyContent='flex-end' display='flex' flexDirection='row'>
            <Form.Buttons.Cta onClick={closeDialogFilters} mt='l' mr='xs'>
              {t('discard')}
            </Form.Buttons.Cta>
            <Form.Buttons.Submit mt='l'>{t('apply')}</Form.Buttons.Submit>
          </Box>
        </form>
      </Box>
    </Box>
  );
};
